import {
  isContainmentOverdue,
  type ContainmentActionStatus,
  type ContainmentEffectiveness,
  type ContainmentPlanAction,
  type ContainmentPriority,
} from "@playbookai/widget-schemas";

export const STATUS_TONE: Record<ContainmentActionStatus, string> = {
  Open: "border-pb-border bg-pb-card-elevated text-pb-text-muted",
  "In progress": "border-pb-cyan/40 bg-pb-cyan/15 text-pb-cyan-text",
  Done: "border-pb-green/40 bg-pb-green/15 text-pb-green-text",
  Stopped:
    "border-pb-border bg-pb-card-elevated text-pb-text-muted line-through",
};

export const PRIORITY_TONE: Record<ContainmentPriority, string> = {
  High: "border-pb-red/40 bg-pb-red/15 text-pb-red-text",
  Medium: "border-pb-amber/40 bg-pb-amber/15 text-pb-amber-text",
  Low: "border-pb-border bg-pb-card-elevated text-pb-text-muted",
};

export const PRIORITY_DOT: Record<ContainmentPriority, string> = {
  High: "bg-pb-red",
  Medium: "bg-pb-amber",
  Low: "bg-pb-text-muted",
};

export const EFFECT_TONE: Record<ContainmentEffectiveness, string> = {
  "Not yet assessed": "border-pb-border bg-pb-card-elevated text-pb-text-muted",
  Effective: "border-pb-green/40 bg-pb-green/15 text-pb-green-text",
  "Partially effective": "border-pb-amber/40 bg-pb-amber/15 text-pb-amber-text",
  "Not effective": "border-pb-red/40 bg-pb-red/15 text-pb-red-text",
};

export const EFFECT_TEXT: Record<ContainmentEffectiveness, string> = {
  "Not yet assessed": "text-pb-text-muted",
  Effective: "text-pb-green-text",
  "Partially effective": "text-pb-amber-text",
  "Not effective": "text-pb-red-text",
};

export const EFFECTIVENESS_SHORT: Record<ContainmentEffectiveness, string> = {
  "Not yet assessed": "Unassessed",
  Effective: "Effective",
  "Partially effective": "Partial",
  "Not effective": "Not eff.",
};

export const PRIORITY_RANK: Record<ContainmentPriority, number> = {
  High: 0,
  Medium: 1,
  Low: 2,
};

export const STATUS_RANK: Record<ContainmentActionStatus, number> = {
  Open: 0,
  "In progress": 1,
  Done: 2,
  Stopped: 3,
};

/** Worst-first: what still needs attention outranks what is already working. */
export const EFFECTIVENESS_RANK: Record<ContainmentEffectiveness, number> = {
  "Not effective": 0,
  "Partially effective": 1,
  "Not yet assessed": 2,
  Effective: 3,
};

export function localDateMs(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return Number.NaN;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date.getTime()
    : Number.NaN;
}

export function shortDate(value: string): string {
  const parsed = localDateMs(value);
  return Number.isNaN(parsed)
    ? value
    : new Date(parsed).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
}

export function noteTime(value: number): string {
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Attention order: what the protection is failing at, then what is late, then
 * what is still open, then priority. Derived at render time — nothing about
 * this ordering is stored.
 */
export function sortByAttention(
  actions: readonly ContainmentPlanAction[],
  now: Date = new Date(),
): ContainmentPlanAction[] {
  return [...actions].sort(
    (a, b) =>
      EFFECTIVENESS_RANK[a.effectiveness] -
        EFFECTIVENESS_RANK[b.effectiveness] ||
      Number(isContainmentOverdue(b, now)) -
        Number(isContainmentOverdue(a, now)) ||
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
      compareEta(a.eta, b.eta) ||
      a.action.localeCompare(b.action),
  );
}

function compareEta(a: string, b: string): number {
  const aMs = localDateMs(a);
  const bMs = localDateMs(b);
  const aValid = Number.isFinite(aMs);
  const bValid = Number.isFinite(bMs);
  if (!aValid || !bValid) return Number(bValid) - Number(aValid);
  return aMs - bMs;
}

/**
 * Reports the recorded effectiveness verdict and nothing else. It deliberately
 * does NOT claim the success criteria failed: the schema stores a judgement
 * (`effectiveness`) and free-text criteria, never an evaluation linking the
 * two, so asserting a criteria failure would be an invention.
 */
export function effectivenessWarning(
  action: ContainmentPlanAction,
): string | null {
  if (action.effectiveness === "Not effective") {
    return "Effectiveness marked as Not effective.";
  }
  if (action.effectiveness === "Partially effective") {
    return "Effectiveness marked as Partially effective.";
  }
  return null;
}

export function hasVerification(action: ContainmentPlanAction): boolean {
  return Boolean(
    action.verificationMethod?.trim() || action.verificationCadence?.trim(),
  );
}
